import { RepoAsync } from "./repoToggle";
import { IUser } from "./IUser";
import { ActiveGamesDisplay } from "./ActiveGamesDisplay";
import { showBoxscoreModal } from "./boxscore";

type LobbyControllerHandlers = {
    loadGame: (gameId: string) => void;
    forfeitGame: (gameId: string, isMyTurn: boolean, updatedAt: string) => void;
}

export class LobbyController {
    private activeGamesDisplay: ActiveGamesDisplay;
    private handlers: LobbyControllerHandlers;
    private lastSeenTime: number;
    private isShowingArchived = false;
    private allGames: any[] = [];
    private currentUser: IUser | null = null;

    constructor(
        private repo: RepoAsync,
        private lobbyElement: HTMLElement,
        handlers: LobbyControllerHandlers,
        lastSeenTime: number
    ) {
        this.handlers = handlers;
        this.lastSeenTime = lastSeenTime;
        const gamesContainer = lobbyElement.querySelector('#active-games-container') as HTMLElement;

        const enclosingContainer = document.createElement('div');
        enclosingContainer.className = 'lobby-toggle-enclosing-container';
        enclosingContainer.style.display = 'flex';
        enclosingContainer.style.alignItems = 'center';
        enclosingContainer.style.justifyContent = 'space-between';

        const gamesTitle = document.createElement('h3');
        gamesTitle.textContent = 'Games';
        gamesTitle.className = 'skeech-lobby-section-title';

        const toggleSwitchAssembly = document.createElement('div');
        toggleSwitchAssembly.style.display = 'flex';
        toggleSwitchAssembly.style.alignItems = 'center';

        const activeLabel = document.createElement('span');
        activeLabel.textContent = 'Active';
        activeLabel.className = 'lobby-toggle-label';

        const toggleContainer = document.createElement('div');
        toggleContainer.className = 'lobby-toggle-container';
        toggleContainer.style.display = 'flex';


        const activeToggle = document.createElement('div');
        activeToggle.className = 'lobby-toggle selected';

        const archivedToggle = document.createElement('div');
        archivedToggle.className = 'lobby-toggle';

        const archivedLabel = document.createElement('span');
        archivedLabel.textContent = 'Archived';
        archivedLabel.className = 'lobby-toggle-label';

        toggleContainer.appendChild(activeToggle);
        toggleContainer.appendChild(archivedToggle);

        toggleSwitchAssembly.appendChild(activeLabel);
        toggleSwitchAssembly.appendChild(toggleContainer);
        toggleSwitchAssembly.appendChild(archivedLabel);

        enclosingContainer.appendChild(gamesTitle);
        enclosingContainer.appendChild(toggleSwitchAssembly);

        gamesContainer.before(enclosingContainer);

        activeToggle.onclick = () => {
            if (this.isShowingArchived) {
                this.isShowingArchived = false;
                activeToggle.classList.add('selected');
                archivedToggle.classList.remove('selected');
                this.renderGames();
            }
        };

        archivedToggle.onclick = () => {
            if (!this.isShowingArchived) {
                this.isShowingArchived = true;
                archivedToggle.classList.add('selected');
                activeToggle.classList.remove('selected');
                this.renderGames();
            }
        };
        
        this.activeGamesDisplay = new ActiveGamesDisplay(
            gamesContainer,
            (game) => {
                const isArchived = game.status === 'completed' || game.status === 'forfeited';
                if (isArchived) {
                    this.showArchivedBoxscore(game);
                    return;
                }
                this.handlers.loadGame(game.id);
            },
            (gameId, isMyTurn, updatedAt) => {
                this.handlers.forfeitGame(gameId, isMyTurn, updatedAt);
            }
        );
    }

    public async enterLobby(currentUser: IUser) {
        this.currentUser = currentUser;
        // Fetch active games once on entry
        // Updates will come from presence sync (~every 15s via Supabase heartbeat)
        await this.refreshGames();
    }

    public async refreshGames() {
        if (!this.currentUser) return;
        // Fetch both active (including forfeited) and archived (completed, non-tournament, non-solo) games
        const [active, archived] = await Promise.all([
            this.repo.listActiveGamesByUser(this.currentUser.id),
            this.repo.listCompletedMultiplayerGamesByUser(this.currentUser.id),
        ]);
        this.allGames = [...active, ...archived];
        this.renderGames();
    }

    private renderGames() {
        if (!this.currentUser) return;
        const gamesToShow = this.allGames.filter(game => {
            const isArchived = game.status === 'completed' || game.status === 'forfeited';
            return this.isShowingArchived ? isArchived : !isArchived;
        });

        // Show the display
        this.activeGamesDisplay.show(gamesToShow, this.currentUser.id, this.lastSeenTime);
    }

    private showArchivedBoxscore(game: any) {
        const p1Name = game?.p1?.user_name || 'Player 1';
        const p2Name = game?.p2?.user_name || 'Player 2';
        const subtitleParts: string[] = [];
        if (game?.updated_at) {
            try {
                subtitleParts.push(new Date(game.updated_at).toLocaleString());
            } catch {}
        }
        if (game?.status === 'forfeited') {
            subtitleParts.push('Forfeit');
        } else if (game?.status === 'completed') {
            subtitleParts.push('Final');
        }
        const subtitle = subtitleParts.filter(Boolean).join(' | ');
        const contextNote = game?.status === 'forfeited' ? 'Game ended by forfeit.' : undefined;

        showBoxscoreModal({
            title: 'Match Summary',
            subtitle,
            contextNote,
            p1Name,
            p2Name,
            p1Rolls: Array.isArray(game?.p1_roll_history) ? game.p1_roll_history : [],
            p2Rolls: Array.isArray(game?.p2_roll_history) ? game.p2_roll_history : [],
        });
    }

    public hide() {
        this.activeGamesDisplay.hide();
    }
}
